import { createSlice } from '@reduxjs/toolkit';
import { coverageAreaApi } from '@store/rtk/coverageArea';
import { idType } from '@src/types';
import {
  CoverageAreaSliceType,
  handleGetData,
  handleRemoveData,
  weekDays,
} from './configs';
import { TABLE_COLUMNS } from '@uikit/Tables/columns';

const { coverageAreaColumns } = TABLE_COLUMNS;

const initialState: CoverageAreaSliceType = {
  states: [],
  countries: [],
  cities: [],
  zipCodes: [],
  coverageAreaTableData: {
    columns: coverageAreaColumns,
    rows: [],
    size: 5,
  },
  businessHoursTableData: null,
};

export const coverageAreaSlice = createSlice({
  name: 'coverageArea',
  initialState,
  reducers: {
    setCoverageAreas: (
      state,
      {
        payload: { selected, children },
      }: {
        payload: {
          selected: Record<string, idType>;
          children: Array<string>;
        };
      }
    ) => {
      const handleState = {
        county: 'countries',
        city: 'cities',
        zip_code: 'zipCodes',
      };

      children.forEach((key) => {
        state[handleState[key]] = handleRemoveData(
          state[handleState[key]],
          selected
        );
      });
    },
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      coverageAreaApi.endpoints.getStates.matchFulfilled,
      (state, { payload }) => {
        if (payload) {
          state.states = handleGetData(state.states, payload, {});
        }
      }
    );
    builder.addMatcher(
      coverageAreaApi.endpoints.getCountries.matchFulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: {
              originalArgs: {
                state: { id, parents },
              },
            },
          },
        }
      ) => {
        if (payload) {
          state.countries = handleGetData(state.countries, payload, {
            ...parents,
            state: id,
          });
        }
      }
    );
    builder.addMatcher(
      coverageAreaApi.endpoints.getCities.matchFulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: {
              originalArgs: {
                county: { id, parents },
              },
            },
          },
        }
      ) => {
        if (payload) {
          state.cities = handleGetData(state.cities, payload, {
            ...parents,
            county: id,
          });
        }
      }
    );
    builder.addMatcher(
      coverageAreaApi.endpoints.getZipCodes.matchFulfilled,
      (
        state,
        {
          payload,
          meta: {
            arg: {
              originalArgs: {
                city: { id, parents },
              },
            },
          },
        }
      ) => {
        if (payload) {
          state.zipCodes = handleGetData(state.zipCodes, payload, {
            ...parents,
            city: id,
          });
        }
      }
    );
    builder.addMatcher(
      coverageAreaApi.endpoints.getCoverageAreaTableData.matchFulfilled,
      (state, { payload }) => {
        state.coverageAreaTableData = {
          ...state.coverageAreaTableData,
          rows: payload.content.map((el) => ({
            ...el,
            id: el.id,
            curb: el.curb,
            door: el.door,
          })),
          last: payload.last,
          page: payload.page,
          size: payload.size,
          totalElements: payload.totalElements,
          requestTablePageChange:
            coverageAreaApi.endpoints.getCoverageAreaTableData,
          totalPages: payload.totalPages,
          table: payload.table,
          filter: payload.filter,
        };
      }
    );
    builder.addMatcher(
      coverageAreaApi.endpoints.getBusinessHoursTableData.matchFulfilled,
      (state, { payload }) => {
        const data = {};
        payload.forEach((el) => {
          const dayNumber = Object.keys(el)[0];

          el[dayNumber].forEach((el) => {
            let hour = String(el);
            if (hour.length === 1) {
              hour = '0' + hour;
            }
            const key = `${weekDays[dayNumber]}-${hour}`;
            data[key] = true;
          });
        });
        state.businessHoursTableData = data;
      }
    );
  },
});

export const { setCoverageAreas } = coverageAreaSlice.actions;
export const selectCoverageArea = (state: {
  coverageArea: CoverageAreaSliceType;
}) => state.coverageArea;
export const coverageAreaReducer = coverageAreaSlice.reducer;
